/* eslint-disable max-len */
import {BehaviorSubject, Observable} from 'rxjs';
import {LinesApi, SharedServicesApi} from '../api';
import {TRANSPORT_GROUP_ID, TRANSPORT_GROUP_KEY_MAP} from '../constants';
import {Itinerary, RealTime, RouteModel, ServiceModel, SharedServiceStatus, StopModel} from '../models';
import {Db} from './db';

export interface RealTimes {
    [stopId: string]: { realTimes: { [routeId: string]: RealTime[] }, timeTables: { [routeId: string]: Itinerary[] }, status: SharedServiceStatus | null };
}

export class RealTimeWorker {

    private _currentStop: StopModel | null = null;
    private _sharedServices: ServiceModel[] = [];
    private _closeStops: StopModel[] = [];
    private _activeTransportSlugs: Set<string> = new Set();

    private publicServicesTimeout?: NodeJS.Timeout;
    private sharedServicesTimeout?: NodeJS.Timeout;
    private closeStopsTimeout?: NodeJS.Timeout;

    private realTimes: BehaviorSubject<RealTimes>;
    public realTimes$: Observable<RealTimes>;

    private lastSlugFilter: any;
    private lastActiveSlugs: Set<string> = new Set();
    private pollingActive = true;

    private static pendingStopRequests: Map<string, Promise<{ realTimes: { [routeId: string]: RealTime[] }, timeTables: { [routeId: string]: Itinerary[] }, status: SharedServiceStatus | null }>> = new Map();


    constructor(private db: Db) {

        this.realTimes = new BehaviorSubject<RealTimes>({});
        this.realTimes$ = this.realTimes.asObservable();
        this.lastSlugFilter = this.db?.dataSources?.stops?.slugFilter;
    }

    set currentStop(stop: StopModel | null) {
        if (this.publicServicesTimeout) clearTimeout(this.publicServicesTimeout);
        this._currentStop = stop;

        if (this._currentStop) this.getPublicServicesRealTimes();
    }

    set sharedServices(services: ServiceModel[]) {
        if (this.sharedServicesTimeout) clearTimeout(this.sharedServicesTimeout);
        this._sharedServices = services;
        if (this._sharedServices.length) this.updateSharedServicesRealTimes();
    }

    set closeStops(stops: StopModel[]) {
        if (this.closeStopsTimeout) clearTimeout(this.closeStopsTimeout);
        this._closeStops = stops;
        if (this._closeStops.length) this.getCloseStopsRealTimes();
    }
    public set activeTransportSlugs(slugs: string[]) {
        this._activeTransportSlugs = new Set(slugs);
    }
    public updateSlugFilter(newSlugFilter: any): void {
        this.getSharedServicesRealTimes();
    }

    public async updateAllSharedServicesRealTimes() {
        console.log('updateAllSharedServicesRealTimes');
        await this.getAllSharedServicesRealTimes();
    }

    public async getPublicServiceStopRealTimes(stop: StopModel): Promise<{ realTimes: { [routeId: string]: RealTime[] }, timeTables: { [routeId: string]: Itinerary[] }, status: SharedServiceStatus | null }> {
        const cacheKey = stop.stopId;
        if (RealTimeWorker.pendingStopRequests.has(cacheKey)) {
            return RealTimeWorker.pendingStopRequests.get(cacheKey)!;
        }

        if (this.db.dataSources && TRANSPORT_GROUP_KEY_MAP[TRANSPORT_GROUP_ID.PUBLIC]?.slugs.includes(stop.slug)) {
            const request = (async () => {
                try {
                    const [realTimes, timetables, routes] = await Promise.all([
                        LinesApi.getStopRealTimes(stop),
                        LinesApi.getStopTimeTable(stop),
                        this.db.dataSources?.routes.findByStop(stop),
                    ]);


                    const data: RealTimes = {
                        [stop.id]: {
                            realTimes: {},
                            timeTables: {},
                            status: null,
                        },
                    };

                    routes?.forEach((route) => {
                        const routeRealTimes = realTimes.filter((realTime) => realTime.lineCode === route.shortName);
                        const routeTimeTables = timetables.find((timeTable) => timeTable.route_id === route.routeId)?.itineraries_timetables || [];

                        data[stop.id].realTimes = {
                            ...data[stop.id].realTimes,
                            [route.id]: (
                                routeRealTimes.length ?
                                    routeRealTimes :
                                    routeTimeTables.reduce(
                                        (arr:any, next:any) => ([...arr, ...this.timeTableToRealTime(route, stop, next)]),
                                        [] as any[],
                                    )
                            ).sort((a:any, b:any) => a.arrivalTime - b.arrivalTime),
                        };
                        data[stop.id].timeTables = {...data[stop.id].timeTables, [route.id]: routeTimeTables};
                    });

                    this.realTimes.next({...this.realTimes.value, ...data});
                    return data[stop.id];
                } catch (error) {
                    console.error('Error en getPublicServiceStopRealTimes:', error);
                    return {
                        realTimes: {},
                        timeTables: {},
                        status: null,
                    };
                } finally {
                    RealTimeWorker.pendingStopRequests.delete(cacheKey);
                }
            })();

            RealTimeWorker.pendingStopRequests.set(cacheKey, request);
            return request;
        }

        return {
            realTimes: {},
            timeTables: {},
            status: null,
        };
    }


    private timeTableToRealTime(route: RouteModel, stop: StopModel, itinerary: Itinerary) {

        const today = itinerary.services_timetables.find((st) => st.todayService);

        const realTimes = today ? today.timetable.reduce((arr, time) => {

            const timeArr = time.split(':').map((n: string) => parseInt(n));
            const date = new Date();
            date.setHours(timeArr[0], timeArr[1], 0, 0);
            return [
                ...arr,
                ...(date.getTime() > Date.now() ? [{
                    destination: itinerary.trips_headsign,
                    latitude: stop.lat,
                    longitude: stop.lng,
                    arrivalTime: (date.getTime() - Date.now()) / 1000,
                    lineCode: route.shortName,
                }] : []),
            ];
        }, [] as any[]) : [];
        return realTimes;
    }

    private async getPublicServicesRealTimes() {

        if (this._currentStop) await this.getPublicServiceStopRealTimes(this._currentStop);

        this.publicServicesTimeout = setTimeout(this.getPublicServicesRealTimes.bind(this), 10000);
    }

    private async getSharedServicesRealTimes() {
        const slugFilter = this.lastSlugFilter;
        if (!slugFilter) {
            return;
        }
        const currentActiveSlugs = new Set(
            this._sharedServices
                .filter((service) =>
                    slugFilter[service.slug] && this._activeTransportSlugs.has(service.slug),
                )
                .map((service) => service.slug),
        );
        const newActiveSlugs = Array.from(currentActiveSlugs).filter(
            (slug) => !this.lastActiveSlugs.has(slug),
        );
        this.lastActiveSlugs = currentActiveSlugs;
        const realTimes: RealTimes = {};
        for (const slug of newActiveSlugs) {
            const service = this._sharedServices.find((s) => s.slug === slug);
            if (!service) continue;
            try {
                const data = await SharedServicesApi.getServiceStatus(service);
                data.forEach((status) => {
                    realTimes[status.id] = {
                        realTimes: {},
                        timeTables: {},
                        status: status,
                    };
                });
            } catch (error) {
                console.error(`Error fetching service status for ${service.slug}:`, error);
            }
        }
        this.realTimes.next({...this.realTimes.value, ...realTimes});
    }

    private async updateSharedServicesRealTimes() {
        const slugFilter = this.db?.dataSources?.stops?.slugFilter;
        if (!slugFilter) {
            return;
        }
        const activeServices = this._sharedServices.filter(
            (service) => slugFilter[service.slug] && this._activeTransportSlugs.has(service.slug),
        );
        const realTimes: RealTimes = {};

        for (const service of activeServices) {
            try {
                const data = await SharedServicesApi.getServiceStatus(service);
                data.forEach((status) => {
                    realTimes[status.id] = {
                        realTimes: {},
                        timeTables: {},
                        status: status,
                    };
                });
            } catch (error) {
                console.error(`Error fetching service status for ${service.slug}:`, error);
            }
        }
        this.realTimes.next({...this.realTimes.value, ...realTimes});
        this.sharedServicesTimeout = setTimeout(this.updateSharedServicesRealTimes.bind(this), 300000);
    }

    private async getAllSharedServicesRealTimes() {

        const realTimes: RealTimes = {};
        const filteredServices = this._sharedServices.filter(
            (service) => this._activeTransportSlugs.has(service.slug),
        );
        for (const service of filteredServices) {
            const data = await SharedServicesApi.getServiceStatus(service);

            data.forEach((status) => {
                realTimes[status.id] = {
                    realTimes: {},
                    timeTables: {},
                    status: status,
                };
            });
        }
        this.realTimes.next({...this.realTimes.value, ...realTimes});
    }
    public stopCloseStopsPolling() {
        this.pollingActive = false;
        if (this.closeStopsTimeout) {
            clearTimeout(this.closeStopsTimeout);
            this.closeStopsTimeout = undefined;
            console.log('Polling timeout cleared');
        }
    }

    public startCloseStopsPolling() {
        this.pollingActive = true;
        this.getCloseStopsRealTimes();
    }

    private async getCloseStopsRealTimes() {
        if (!this._closeStops.length) return;
        const isServeisAprop = window.location.pathname.includes('serveis-aprop');
        if (!isServeisAprop && !this._currentStop) return;

        for (const stop of this._closeStops) {
            if (isServeisAprop || stop.id !== this._currentStop?.id) {
                await this.getPublicServiceStopRealTimes(stop);
            }
        }

        if (this._closeStops.length && this.pollingActive) {
            this.closeStopsTimeout = setTimeout(this.getCloseStopsRealTimes.bind(this), 10000);
        }
    }
}
